import { MatchListType } from '@okletsplay/core';
import { createSelector } from '@reduxjs/toolkit';
import { EntityStoreName } from 'app/enums/store';
import { AppState } from 'app/interfaces/store';
import { useMemo } from 'react';
import { store } from 'redux/store';

import { entityAdapter } from './adapter';
import {
  MatchAccessibility,
  MatchPlayerRole,
  MatchPlayerStatus,
  MatchStatus,
} from '@okletsplay/core/src/modules/match/enums';
import { sessionSelector } from 'app/store/session/selectors';

const matchesState = (state: AppState) => state[EntityStoreName.Matches];

const selectAll = entityAdapter.getSelectors<AppState>(
  (state) => state[EntityStoreName.Matches]
).selectAll;

const selectOpen = createSelector(selectAll, sessionSelector.activeUser, (matches, user) =>
  matches.filter(
    (match) =>
      [MatchStatus.Created, MatchStatus.Pending].includes(match.status) &&
      match.accessibility === MatchAccessibility.Public &&
      match.players.find((player) => player.userId === user.userId)?.role == MatchPlayerRole.Owner
  )
);

const selectFeatured = createSelector(selectAll, (matches) =>
  matches.filter((match) => match.isFeatured)
);

const selectMatchCount = createSelector(selectAll, (matches) => matches.length);

const selectPending = createSelector(selectAll, sessionSelector.activeUser, (matches, user) =>
  matches.filter((match) => {
    const currentPlayerStatus = match.players.find(
      (player) => player.userId === user.userId
    )?.status;

    return (
      [MatchStatus.Live, MatchStatus.Lobby].includes(match.status) ||
      (match.accessibility === MatchAccessibility.Private &&
        [MatchStatus.Pending].includes(match.status)) ||
      (currentPlayerStatus &&
        match.accessibility === MatchAccessibility.Public &&
        [MatchPlayerStatus.Requested, MatchPlayerStatus.Invited].includes(currentPlayerStatus))
    );
  })
);

const selectByIdFactory = (id: string) =>
  createSelector(store.getState, (state) =>
    entityAdapter.getSelectors<AppState>(matchesState).selectById(state, String(id))
  );

const selectTotalFactory = (type: MatchListType | string) =>
  createSelector(matchesState, (state) => (type ? state.pagination[type]?.total ?? 0 : 0));

export const matchesSelector = {
  all: selectAll,
  open: selectOpen,
  pending: selectPending,
  featured: selectFeatured,
  matchCount: selectMatchCount,
};

export const matchesSelectorFactory = {
  useById: (id: string) => useMemo(() => selectByIdFactory(id), [id]),
  useTotal: (type: MatchListType | string) => useMemo(() => selectTotalFactory(type), [type]),
};
